import { Card, CardContent } from "@b2b/ui-components";
import { Badge } from "@b2b/ui-components";
import { Button } from "@b2b/ui-components";
import { Star, Heart } from "lucide-react";
import { useState, useEffect } from "react";
import { getProductDetailShareUrl } from "../../lib/productRoutes";

// Placeholder image as data URI (no external request needed)
const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect fill='%23e5e7eb' width='400' height='400'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='18' fill='%239ca3af'%3ENo Image%3C/text%3E%3C/svg%3E";

interface ProductCardProps {
  product: {
    id: string;
    slug?: string;
    title: string;
    price: string;
    /** e.g. "Per pieces" — shown below price (product detail parity). */
    unitPrice?: string;
    moq: string;
    score: number;
    supplier: {
      name: string;
      rating: number;
      verified: boolean;
    };
    image: string;
  };
  isFavorited?: boolean;
  onFavoriteToggle?: (productId: string, isFavorited: boolean) => Promise<void>;
  showFavoriteIcon?: boolean;
  /** Image overlay "Request a Quote" on hover. Default true. */
  showRequestQuote?: boolean;
  /**
   * When true (and `showRequestQuote` is false), shows a primary "View product" button
   * on the image in the same hover overlay slot as "Request a Quote".
   */
  showViewProductOnHover?: boolean;
}

export const ProductCard = ({
  product,
  isFavorited = false,
  onFavoriteToggle,
  showFavoriteIcon = false,
  showRequestQuote = true,
  showViewProductOnHover = false,
}: ProductCardProps) => {
  const { title, price, unitPrice, moq, score, supplier, image } = product;
  const [favoriteState, setFavoriteState] = useState(isFavorited);
  const [isToggling, setIsToggling] = useState(false);

  // Sync favorite state when prop changes
  useEffect(() => {
    setFavoriteState(isFavorited);
  }, [isFavorited]);

  const renderStars = (rating: number) =>
    Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-3 w-3 ${
          i < rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
        }`}
      />
    ));

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click navigation
    
    if (!onFavoriteToggle || isToggling) return;

    setIsToggling(true);
    const newFavoriteState = !favoriteState;
    
    // Optimistic update
    setFavoriteState(newFavoriteState);

    try {
      await onFavoriteToggle(product.id, newFavoriteState);
    } catch (error) {
      // Revert on error
      setFavoriteState(!newFavoriteState);
      console.error("Error toggling favorite:", error);
    } finally {
      setIsToggling(false);
    }
  };

  const handleCardClick = () => {
    const url = getProductDetailShareUrl(product);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleRequestQuote = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const rfqUrl = `${window.location.origin}/buyer/rfq_create_form?productId=${product.id}`;
    window.open(rfqUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Card
      className="group hover:shadow-xl hover:border-orange-500 transition-all duration-300 border border-gray-200 rounded-lg overflow-hidden cursor-pointer bg-white"
      onClick={handleCardClick}
    >
      <CardContent className="p-0">
        {/* Product Image - Alibaba Style: Larger & More Prominent */}
        <div className="relative w-full aspect-square overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
          <img
            src={image || PLACEHOLDER_IMAGE}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            onError={(e) => {
              (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
            }}
          />

          {/* Score Badge - Top Right (Alibaba Style) */}
          {/* <div className="absolute top-2 right-2 bg-orange-600 text-white px-2 py-1 rounded text-xs font-semibold shadow-lg">
            {score.toFixed(1)}/5
          </div> */}

          {/* Favorite Icon - Top Left */}
          {showFavoriteIcon && (
            <button
              onClick={handleFavoriteClick}
              disabled={isToggling}
              className="absolute top-2 left-2 p-1.5 rounded-full bg-white/95 hover:bg-white shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed z-10"
              aria-label={favoriteState ? "Remove from favorites" : "Add to favorites"}
            >
              <Heart
                className={`h-4 w-4 transition-colors ${
                  favoriteState
                    ? "fill-red-500 text-red-500"
                    : "text-gray-600 hover:text-red-500"
                }`}
              />
            </button>
          )}

          {(showRequestQuote || showViewProductOnHover) && (
            <div className="absolute inset-x-3 bottom-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              {showRequestQuote ? (
                <Button
                  variant="primary"
                  size="sm"
                  className="w-full"
                  onClick={handleRequestQuote}
                >
                  Request a Quote
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCardClick();
                  }}
                >
                  View product
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Product Info - Alibaba Style: Cleaner Hierarchy */}
        <div className="p-4 space-y-2">
          {/* Product Title - More Prominent */}
          <h3 className="font-semibold text-base text-gray-900 line-clamp-2 leading-tight min-h-[2.5rem] group-hover:text-orange-600 transition-colors">
            {title}
          </h3>

          {/* Price - Large & Bold */}
          <div>
            <div className="text-xl font-bold text-orange-600">{price}</div>
            {unitPrice ? (
              <p className="mt-0.5 text-xs text-gray-600">{unitPrice}</p>
            ) : null}
          </div>

          {/* MOQ & Supplier Info - Compact */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-xs text-gray-500 truncate">MOQ: {moq}</span>
              {supplier.verified && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 border-green-200">
                  Verified
                </Badge>
              )}
            </div>
            <div className="flex items-center space-x-0.5 flex-shrink-0">
              {/* {renderStars(supplier.rating)} */}
            </div>
          </div>

          {/* Supplier Name - Compact */}
          <div className="text-xs text-gray-500 truncate">
            {supplier.name}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
